"use client";

import React, { useState } from "react";
import { Compass, FileCode2, Target, Wrench, TrendingUp, ArrowRight, ArrowLeft, CheckCircle2, Check } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
const stagesData = [{
  id: 1,
  number: "01",
  shortName: "Discovery",
  title: "Discovery and commercial priorities",
  focusNote: "Business alignment and priority product categories",
  description: "A strategic discovery call to understand your business priorities: which product lines deliver the highest margins, target geographical markets (starting with the UK, expanding to international English-speaking regions where relevant), current marketing channels, and where organic search fits. I define realistic success metrics with you—such as organic revenue from specific collections or reducing reliance on paid ad spend—rather than chasing vanity traffic.",
  keyActions: ["Catalog margin review and flagship product prioritisation", "UK and international target market scope definition", "Current paid versus organic channel dependency review", "Commercial KPI definition tied to real business goals"],
  icon: Compass
}, {
  id: 2,
  number: "02",
  shortName: "Technical Audit",
  title: "Technical and competitor audit",
  focusNote: "Platform crawl efficiency and search competitor analysis",
  description: "A deep technical SEO review and competitor analysis tailored to your specific platform and catalogue size. I examine faceted navigation, crawl traps, canonical tags, indexation health, mobile usability, Core Web Vitals, and structured data validation. The deliverable is a prioritised list of fixes ordered strictly by commercial and revenue impact, not a generic automated audit checklist.",
  keyActions: ["Faceted filter, canonical tag, and crawl budget evaluation", "Product, offer, review, and breadcrumb schema validation", "Search competitor analysis comparing category visibility and gaps", "Prioritised action list weighted by commercial revenue impact"],
  icon: FileCode2
}, {
  id: 3,
  number: "03",
  shortName: "Strategy & Mapping",
  title: "Ecommerce keyword and page strategy",
  focusNote: "Intent-based keyword mapping across the catalogue",
  description: "Comprehensive keyword research mapping high-intent queries to product pages, category pages, and supporting content based on actual search demand. I determine where long-tail product terms belong, where category pages can capture non-branded commercial traffic, and when to create dedicated buying guides or comparison pages instead of overloading existing product listings.",
  keyActions: ["Mapping product-specific long-tail terms to individual SKUs", "Structuring category and subcategory target keyword clusters", "Identifying commercial gaps for buying guides and comparisons", "Delivering a clear keyword-to-page architecture map"],
  icon: Target
}, {
  id: 4,
  number: "04",
  shortName: "Implementation",
  title: "Implementation and optimisation",
  focusNote: "Practical execution on pages and platform settings",
  description: "Executing recommendations across revenue-critical pages. I can work alongside your internal developers and ecommerce managers with clear, developer-ready briefs, or make direct changes within platforms like Shopify or WooCommerce when access is granted. Priority is given to high-margin collections and primary landing pages to address critical bottlenecks first.",
  keyActions: ["Product titles, H1s, unique descriptions, and metadata optimisation", "Category introductory copy and hierarchical internal linking", "Redirect maps for discontinued products or catalogue updates", "Developer-ready tickets and direct CMS implementation support"],
  icon: Wrench
}, {
  id: 5,
  number: "05",
  shortName: "Continuous Growth",
  title: "Measurement and continuous improvement",
  focusNote: "Data-led refinement and directional reporting",
  description: "Regular review cycles analysing Google Search Console data, analytics, and ecommerce revenue trends to refine strategy over time. Organic search compounds with consistent investment. I evaluate which landing pages drive qualified traffic and transactions, tuning internal links, updating content, and resolving new technical issues as your catalogue expands.",
  keyActions: ["Tracking organic sessions, commercial rankings, and revenue", "Directional Search Console impression and click analysis", "Iterative refinement of category copy and internal linking paths", "Adapting catalogue architecture as stock and seasonal demand change"],
  icon: TrendingUp
}];
export default function Process() {
  const [activeStageIndex, setActiveStageIndex] = useState<number>(0);
  const currentStage = stagesData[activeStageIndex];
  const totalStages = stagesData.length;
  const goToPrev = () => {
    setActiveStageIndex(prev => prev > 0 ? prev - 1 : prev);
  };
  const goToNext = () => {
    setActiveStageIndex(prev => prev < totalStages - 1 ? prev + 1 : prev);
  };
  return <section id="ecommerceseoprocess" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 px-4 sm:px-6 lg:px-8 border-b border-border transition-colors duration-200">
      <div className="max-w-7xl mx-auto">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="outline" className="mb-4 px-3 py-1 border-primary/30 text-primary font-medium rounded-full">
            Structured Consulting Framework
          </Badge>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight font-default text-foreground">
            My five-stage ecommerce SEO consulting method
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            A methodical, five-stage framework adapted to your catalogue scale, platform setup, and commercial goals. Every stage is focused on commercial priorities, not generic checklists.
          </p>
        </div>

        {/* Stepper Progress & Navigation Buttons */}
        <div className="mb-10 max-w-4xl mx-auto">
          {/* Progress bar */}
          <div className="w-full bg-muted rounded-full h-2 mb-6 overflow-hidden">
            <div className="bg-primary h-2 rounded-full transition-all duration-300 ease-out" style={{
            width: `${(activeStageIndex + 1) / totalStages * 100}%`
          }} role="progressbar" aria-valuenow={activeStageIndex + 1} aria-valuemin={1} aria-valuemax={totalStages} aria-label={`Stage ${activeStageIndex + 1} of ${totalStages}`} />
          </div>

          {/* Stepper Tabs */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            {stagesData.map((stage, i) => {
            const isActive = i === activeStageIndex;
            const isPassed = i < activeStageIndex;
            return <Button key={stage.id} data-index={i} variant={isActive ? "default" : "outline"} size="sm" onClick={() => setActiveStageIndex(i)} aria-pressed={isActive} className={`rounded-full px-3.5 sm:px-4 py-1.5 text-xs sm:text-sm font-medium transition-all flex items-center gap-2 ${isActive ? "shadow-xs" : isPassed ? "border-primary/40 text-foreground hover:bg-primary/5" : "text-muted-foreground hover:text-foreground"}`}>
                  <span className={`size-5 rounded-full flex items-center justify-center text-[11px] font-mono font-semibold ${isActive ? "bg-primary-foreground text-primary" : isPassed ? "bg-primary/15 text-primary" : "bg-muted text-muted-foreground"}`}>
                    {isPassed ? <Check className="size-3" /> : stage.number}
                  </span>
                  <span className="hidden sm:inline">{stage.shortName}</span>
                </Button>;
          })}
          </div>
        </div>

        {/* Active Stage Detailed Card */}
        <div className="max-w-4xl mx-auto mb-12">
          <Card className="border border-border/80 bg-card shadow-xs overflow-hidden">
            <CardHeader className="pb-4 border-b border-border/60 bg-muted/20">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div className="flex items-center gap-3">
                  <div className="size-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <currentStage.icon className="size-5" />
                  </div>
                  <div>
                    <span className="text-xs font-semibold uppercase tracking-wider text-primary">
                      Stage {activeStageIndex + 1} of {totalStages}
                    </span>
                    <CardTitle className="text-xl sm:text-2xl font-semibold font-default text-foreground mt-0.5">
                      {currentStage.title}
                    </CardTitle>
                  </div>
                </div>
                <Badge variant="secondary" className="self-start sm:self-center font-mono text-xs">
                  {currentStage.focusNote}
                </Badge>
              </div>
            </CardHeader>

            <CardContent className="pt-6 space-y-6">
              <div>
                <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-2">
                  Stage Overview
                </h4>
                <p className="text-sm sm:text-base text-foreground leading-relaxed font-default">
                  {currentStage.description}
                </p>
              </div>

              <div className="pt-4 border-t border-border/60">
                <h4 className="text-xs uppercase tracking-wider text-muted-foreground font-semibold mb-3">
                  Key Consulting Activities
                </h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {currentStage.keyActions.map((action, idx) => <div key={`action-${idx}`} data-index={idx} className="p-3 rounded-lg border border-border bg-background/80 flex items-start gap-2.5 text-xs sm:text-sm text-foreground">
                      <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                      <span>{action}</span>
                    </div>)}
                </div>
              </div>

              {/* Stepper Navigation Buttons */}
              <div className="pt-6 border-t border-border/60 flex items-center justify-between">
                <Button variant="outline" size="sm" onClick={goToPrev} disabled={activeStageIndex === 0} className="rounded-full gap-1.5">
                  <ArrowLeft className="size-3.5" />
                  <span>Previous stage</span>
                </Button>

                <span className="text-xs font-mono text-muted-foreground">
                  Stage {activeStageIndex + 1} of {totalStages}
                </span>

                <Button variant={activeStageIndex === totalStages - 1 ? "outline" : "default"} size="sm" onClick={goToNext} disabled={activeStageIndex === totalStages - 1} className="rounded-full gap-1.5">
                  <span>Next stage</span>
                  <ArrowRight className="size-3.5" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* How I Work Through Each Stage Note */}
        <div className="max-w-4xl mx-auto rounded-xl border border-border/70 bg-muted/30 p-6 mb-16 text-left">
          <h3 className="text-base font-semibold text-foreground font-default mb-2">
            How I work through each stage
          </h3>
          <p className="text-sm text-muted-foreground leading-relaxed">
            I deliver all recommendations in plain English, with clear explanations of how each task impacts commercial performance and search discovery. Strategy is strictly tied to commercial goals, revenue-critical product lines, and catalogue health. Search engines continuously evolve, so I do not offer guaranteed rankings, conversion rates, or sales figures; instead, I provide transparent analysis, directional reporting, and consistent strategic iteration.
          </p>
        </div>

        {/* Closing CTA Banner */}
        <div className="rounded-2xl bg-card border border-border p-8 md:p-10 shadow-xs flex flex-col md:flex-row items-center justify-between gap-8 max-w-5xl mx-auto">
          <div className="space-y-2 text-center md:text-left max-w-xl">
            <h3 className="text-xl md:text-2xl font-semibold text-foreground tracking-tight font-default">
              Discuss your store&apos;s commercial SEO priorities
            </h3>
            <p className="text-muted-foreground text-sm md:text-base leading-relaxed font-default">
              Whether you need a full catalogue audit or ongoing monthly consulting, I can review where your online store stands.
            </p>
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full md:w-auto">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/ContactPage" className="inline-flex items-center justify-center gap-2">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="w-4 h-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/ContactPage?topic=free-seo-audit">
                <span>Get a Free SEO Audit</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}